import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
} from 'lucide-react';
import { getSupabaseAdminClient, NameSuggestion } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';
import { BabyNameForm, BabyNameFormValues } from './BabyNameForm';

const supabase = getSupabaseAdminClient();

type SuggestionStatus = 'all' | 'pending' | 'approved' | 'rejected';

// name_suggestions liefert NUR name, gender, status, is_duplicate_aware, admin_notes,
// reviewed_by/at, suggested_by (siehe Migration 002). Es gibt KEINE Spalten origin/
// meaning/reason — der echte Typ aus types.ts ist hier maßgeblich. Zusätzlich wird in
// der Query das Profil des Einreichers eingebunden (Join), daher die user-Erweiterung.
type SuggestionRow = NameSuggestion & {
  user?: { email: string; display_name: string | null } | null;
};

export function NameSuggestionsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SuggestionStatus>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  // Vorschlag, der gerade im Genehmigen-Pop-up bearbeitet wird (null = geschlossen).
  const [approvingSuggestion, setApprovingSuggestion] = useState<SuggestionRow | null>(null);
  const pageSize = 10;
  const queryClient = useQueryClient();

  // Fetch name suggestions with pagination
  const { data: suggestionsData, isLoading } = useQuery({
    queryKey: ['name-suggestions', currentPage, searchQuery, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('name_suggestions')
        .select(`
          *,
          user:profiles!name_suggestions_suggested_by_fkey(email, display_name)
        `, { count: 'exact' });

      // Apply search filter
      if (searchQuery) {
        query = query.ilike('name', `%${searchQuery}%`);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        suggestions: data as SuggestionRow[],
        totalCount: count || 0,
      };
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['name-suggestions-stats'],
    queryFn: async () => {
      const [pendingResult, approvedResult, rejectedResult] = await Promise.all([
        supabase.from('name_suggestions').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('name_suggestions').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('name_suggestions').select('id', { count: 'exact', head: true }).eq('status', 'rejected'),
      ]);

      return {
        pending: pendingResult.count || 0,
        approved: approvedResult.count || 0,
        rejected: rejectedResult.count || 0,
      };
    },
  });

  // Approve mutation
  //
  // Die Werte stammen jetzt vollständig aus dem Genehmigen-Formular (BabyNameForm),
  // NICHT mehr aus dem Vorschlag — name_suggestions hat keine Spalten origin/meaning.
  // Dadurch wird die NOT-NULL-Spalte baby_names.origin garantiert gefüllt (Fix für
  // den bisherigen 400 / Postgres 23502).
  const approveMutation = useMutation({
    mutationFn: async ({
      suggestionId,
      values,
    }: {
      suggestionId: string;
      values: BabyNameFormValues;
    }) => {
      // Neuen Namen in die Datenbank aufnehmen. `values` ist vollständig über
      // BabyNameFormValues typisiert; der `as any`-Cast auf den Query-Builder ist nur
      // der projektweite Workaround für die fehlerhaften Supabase-Generics (vgl.
      // ManualProductsPage/FAQItemsPage) — er maskiert KEINE fehlenden Felder mehr.
      const { error: insertError } = await (supabase.from('baby_names') as any).insert(values);
      if (insertError) throw insertError;

      // Vorschlag als genehmigt markieren.
      const { error: updateError } = await (supabase.from('name_suggestions') as any)
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', suggestionId);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['name-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['name-suggestions-stats'] });
      queryClient.invalidateQueries({ queryKey: ['names'] });
      queryClient.invalidateQueries({ queryKey: ['names-stats'] });
      setApprovingSuggestion(null);
      toast.success('Name wurde genehmigt und zur Datenbank hinzugefügt');
    },
    onError: (error: unknown) => {
      // Unique-Constraint (name, gender) → Name existiert bereits. Modal bleibt offen,
      // der Vorschlag wird NICHT automatisch geändert, damit der Admin reagieren kann.
      if ((error as { code?: string })?.code === '23505') {
        toast.error('Name existiert bereits in der Datenbank');
        return;
      }
      toast.error('Fehler beim Genehmigen des Namens');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase
        .from('name_suggestions')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
        } as never)
        .eq('id', suggestionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['name-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['name-suggestions-stats'] });
      toast.success('Vorschlag wurde abgelehnt');
    },
    onError: () => {
      toast.error('Fehler beim Ablehnen des Vorschlags');
    },
  });

  const totalPages = Math.ceil((suggestionsData?.totalCount || 0) / pageSize);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getGenderBadge = (gender: string) => {
    switch (gender) {
      case 'male':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            Männlich
          </span>
        );
      case 'female':
        return (
          <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded-full">
            Weiblich
          </span>
        );
      case 'neutral':
        return (
          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
            Neutral
          </span>
        );
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            Ausstehend
          </span>
        );
      case 'approved':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            Genehmigt
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
            <XCircle className="w-3 h-3" />
            Abgelehnt
          </span>
        );
      default:
        return null;
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Community-Vorschläge</h1>
        <p className="text-slate-600">Von Nutzern vorgeschlagene Namen prüfen</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.pending || 0}</div>
              <div className="text-sm text-slate-600">Ausstehend</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.approved || 0}</div>
              <div className="text-sm text-slate-600">Genehmigt</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.rejected || 0}</div>
              <div className="text-sm text-slate-600">Abgelehnt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach Name suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as SuggestionStatus);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="approved">Genehmigt</option>
              <option value="rejected">Abgelehnt</option>
            </select>
          </div>
        </div>
      </div>

      {/* Suggestions Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Geschlecht</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Eingereicht von</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Datum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Lädt...
                  </td>
                </tr>
              ) : suggestionsData?.suggestions?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Keine Vorschläge gefunden
                  </td>
                </tr>
              ) : (
                suggestionsData?.suggestions?.map((suggestion) => (
                  <tr key={suggestion.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-900">{suggestion.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getGenderBadge(suggestion.gender)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {suggestion.user?.display_name || suggestion.user?.email || 'Unbekannt'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{formatDate(suggestion.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(suggestion.status)}
                    </td>
                    <td className="px-4 py-3">
                      {suggestion.status === 'pending' ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setApprovingSuggestion(suggestion)}
                            className="p-1.5 bg-emerald-100 hover:bg-emerald-200 rounded text-emerald-700"
                            title="Genehmigen"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => rejectMutation.mutate(suggestion.id)}
                            disabled={rejectMutation.isPending}
                            className="p-1.5 bg-red-100 hover:bg-red-200 rounded text-red-700"
                            title="Ablehnen"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">
                          {suggestion.reviewed_at && formatDate(suggestion.reviewed_at)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, suggestionsData?.totalCount || 0)} von {suggestionsData?.totalCount?.toLocaleString('de-DE') || 0}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border border-slate-300 rounded ${
                  currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'hover:bg-slate-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Genehmigen-Pop-up: name + gender aus dem Vorschlag vorbefüllt, locale Default 'de',
          Rest (inkl. Pflichtfeld origin) trägt der Admin nach. */}
      {approvingSuggestion && (
        <BabyNameForm
          title="Vorschlag genehmigen"
          submitLabel="Genehmigen & hinzufügen"
          isLoading={approveMutation.isPending}
          initialValues={{
            name: approvingSuggestion.name,
            gender: approvingSuggestion.gender,
            locale: 'de',
          }}
          onSubmit={(values) =>
            approveMutation.mutate({ suggestionId: approvingSuggestion.id, values })
          }
          onCancel={() => setApprovingSuggestion(null)}
        />
      )}
    </div>
  );
}
